import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  DollarSign, Users, TrendingUp, TrendingDown, 
  Crown, Star, Heart, RefreshCw, Calendar
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, Legend
} from "recharts";
import AdminLayout from "@/components/AdminLayout";

const COLORS = {
  free: "#9CA3AF",
  recovery_plus: "#FAB710",
  recovery_elite: "#1C3A7A",
  // Legacy support
  gold: "#FAB710",
  platinum: "#1C3A7A",
  revenue: "#22C55E"
};

const AdminSubscriptions = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchData();
  }, [days]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/analytics/subscriptions?days=${days}`);
      setData(response.data);
    } catch (error) {
      toast.error("Failed to load subscription analytics");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const [campaignPreview, setCampaignPreview] = useState(null);
  const [campaignRunning, setCampaignRunning] = useState(false);
  const [campaignResult, setCampaignResult] = useState(null);

  const previewTrialCampaign = async () => {
    try {
      const res = await api.get("/admin/trials/early-member-preview");
      setCampaignPreview(res.data);
    } catch (e) {
      toast.error("Failed to load campaign preview");
    }
  };

  const runTrialCampaign = async () => {
    if (!window.confirm(`Grant a 7-day Recovery+ trial and email ${campaignPreview?.eligible_count} users? This cannot be undone.`)) return;
    setCampaignRunning(true);
    try {
      const res = await api.post("/admin/trials/grant-early-member");
      setCampaignResult(res.data);
      setCampaignPreview(null);
      toast.success(`Trial granted to ${res.data.granted} users, ${res.data.emails_sent} emails sent`);
      fetchData();
    } catch (e) {
      toast.error("Campaign failed — check backend logs");
    } finally {
      setCampaignRunning(false);
    }
  };

  const [coinPreview, setCoinPreview] = useState(null);
  const [coinRunning, setCoinRunning] = useState(false);
  const [coinResult, setCoinResult] = useState(null);

  const previewCoinMigration = async () => {
    try {
      const res = await api.get("/admin/coins/legacy-preview");
      setCoinPreview(res.data);
    } catch (e) {
      toast.error("Failed to load winnings preview");
    }
  };

  const runCoinMigration = async () => {
    if (!window.confirm(`Restore ${coinPreview?.total_coins} coins to ${coinPreview?.eligible_count} users? This runs once per user.`)) return;
    setCoinRunning(true);
    try {
      const res = await api.post("/admin/coins/migrate-legacy");
      setCoinResult(res.data);
      setCoinPreview(null);
      toast.success(`Restored ${res.data.total_coins_restored} coins to ${res.data.users_migrated} users`);
    } catch (e) {
      toast.error("Migration failed — check backend logs");
    } finally {
      setCoinRunning(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <AdminLayout>
      <div className="bg-gray-50 flex items-center justify-center min-h-[calc(100vh-120px)]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#1C3A7A] border-t-transparent"></div>
      </div>
      </AdminLayout>
    );
  }

  const pieData = data ? [
    { name: "Free", value: data.distribution.free?.count || 0, color: COLORS.free },
    { name: "Recovery+", value: (data.distribution.recovery_plus?.count || 0) + (data.distribution.gold?.count || 0), color: COLORS.recovery_plus },
    { name: "Elite", value: (data.distribution.recovery_elite?.count || 0) + (data.distribution.platinum?.count || 0), color: COLORS.recovery_elite }
  ] : [];

  return (
    <AdminLayout>
      <div className="bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1C3A7A] to-[#2d4a8a] px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-xl font-bold text-white">Subscription Analytics</h1>
                <p className="text-white/70 text-sm">Revenue & subscriber insights</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                className="bg-white/10 text-white border-white/20 rounded-lg px-3 py-2 text-sm"
            >
              <option value={7} className="text-gray-900">Last 7 days</option>
              <option value={30} className="text-gray-900">Last 30 days</option>
              <option value={90} className="text-gray-900">Last 90 days</option>
              <option value={365} className="text-gray-900">Last year</option>
            </select>
            <Button
              onClick={fetchData}
              variant="outline"
              size="sm"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* MRR Card */}
          <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Monthly Revenue (Est.)</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(data?.summary.estimated_mrr || 0)}</p>
                <p className="text-xs text-gray-400 mt-1">ARR: {formatCurrency(data?.summary.estimated_arr || 0)}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          {/* Total Subscribers Card */}
          <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-[#FAB710]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Active Subscribers</p>
                <p className="text-2xl font-bold text-gray-900">{data?.summary.total_subscribers || 0}</p>
                <p className="text-xs text-gray-400 mt-1">of {data?.summary.total_users || 0} total users</p>
              </div>
              <div className="w-12 h-12 bg-[#FAB710]/10 rounded-full flex items-center justify-center">
                <Crown className="w-6 h-6 text-[#FAB710]" />
              </div>
            </div>
          </div>

          {/* Conversion Rate Card */}
          <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-[#1C3A7A]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Conversion Rate</p>
                <p className="text-2xl font-bold text-gray-900">{data?.summary.conversion_rate || 0}%</p>
                <p className="text-xs text-gray-400 mt-1">Free → Paid</p>
              </div>
              <div className="w-12 h-12 bg-[#1C3A7A]/10 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-[#1C3A7A]" />
              </div>
            </div>
          </div>

          {/* Churn Rate Card */}
          <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Churn Rate</p>
                <p className="text-2xl font-bold text-gray-900">{data?.summary.churn_rate || 0}%</p>
                <p className="text-xs text-gray-400 mt-1">{data?.period_stats.churned || 0} churned ({days}d)</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm">
          <div className="border-b border-gray-200 px-4">
            <nav className="flex gap-6">
              {["overview", "trends", "breakdown"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 text-sm font-medium border-b-2 transition-colors capitalize ${
                    activeTab === tab
                      ? "border-[#1C3A7A] text-[#1C3A7A]"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === "overview" && (
              <>
              <div className="mb-6 rounded-xl p-6" style={{ backgroundColor: "#0A2A5A" }} data-testid="trial-campaign-card">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: "#F9AB0D" }}>Growth Campaign</p>
                    <h3 className="text-lg font-semibold text-white">Early Member Thank-You Trial</h3>
                    <p className="text-sm mt-1" style={{ color: "rgba(255,255,255,0.7)" }}>
                      Grant every real free user a 7-day Recovery+ trial and send them a thank-you email. Seeded, test and already-subscribed users are excluded automatically.
                    </p>
                    {campaignPreview && (
                      <p className="text-sm mt-2 font-semibold" style={{ color: "#F9AB0D" }} data-testid="trial-preview-count">
                        {campaignPreview.eligible_count} eligible users will receive the trial + email
                      </p>
                    )}
                    {campaignResult && (
                      <p className="text-sm mt-2 font-semibold text-green-400" data-testid="trial-campaign-result">
                        ✓ Done — {campaignResult.granted} trials granted, {campaignResult.emails_sent} emails sent
                        {campaignResult.email_failures?.length > 0 && ` (${campaignResult.email_failures.length} email failures)`}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {!campaignPreview ? (
                      <Button onClick={previewTrialCampaign} variant="outline" className="border-white/30 text-white hover:bg-white/10 bg-transparent" data-testid="trial-preview-btn">
                        Preview Campaign
                      </Button>
                    ) : (
                      <Button onClick={runTrialCampaign} disabled={campaignRunning || campaignPreview.eligible_count === 0} style={{ backgroundColor: "#F9AB0D", color: "#0A2A5A" }} className="font-bold hover:opacity-90" data-testid="trial-run-btn">
                        {campaignRunning ? "Sending..." : `Launch — ${campaignPreview.eligible_count} users`}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              <div className="mb-6 rounded-xl p-6 border border-amber-300 bg-amber-50" data-testid="coin-migration-card">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest mb-1 text-amber-600">One-Time Fix</p>
                    <h3 className="text-lg font-semibold text-gray-900">Restore Lost Spin Winnings</h3>
                    <p className="text-sm mt-1 text-gray-600">
                      Converts coins stuck in the old (unspendable) balance field into real Gift Shop coins. Safe to run anytime — each user is only migrated once.
                    </p>
                    {coinPreview && (
                      <p className="text-sm mt-2 font-semibold text-amber-700" data-testid="coin-preview-count">
                        {coinPreview.eligible_count} users owed {coinPreview.total_coins} coins
                      </p>
                    )}
                    {coinResult && (
                      <p className="text-sm mt-2 font-semibold text-green-600" data-testid="coin-migration-result">
                        ✓ Done — {coinResult.total_coins_restored} coins restored to {coinResult.users_migrated} users
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {!coinPreview ? (
                      <Button onClick={previewCoinMigration} variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-100 bg-transparent" data-testid="coin-preview-btn">
                        Preview
                      </Button>
                    ) : (
                      <Button onClick={runCoinMigration} disabled={coinRunning || coinPreview.eligible_count === 0} className="font-bold bg-amber-500 hover:bg-amber-600 text-white" data-testid="coin-migrate-btn">
                        {coinRunning ? "Restoring..." : `Restore Winnings — ${coinPreview.eligible_count} users`}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Subscription Distribution Pie Chart */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscription Distribution</h3>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => [value, "Users"]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex justify-center gap-6 mt-4">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.free }}></div>
                      <span className="text-sm text-gray-600">Free ({data?.distribution.free.count})</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.gold }}></div>
                      <span className="text-sm text-gray-600">Recovery+ ({data?.distribution.recovery_plus?.count || 0})</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.platinum }}></div>
                      <span className="text-sm text-gray-600">Recovery Elite ({data?.distribution.recovery_elite?.count || 0})</span>
                    </div>
                  </div>
                </div>

                {/* Period Stats */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Period Summary ({days} days)</h3>
                  <div className="space-y-4">
                    <div className="bg-gradient-to-r from-[#FAB710]/10 to-[#FAB710]/5 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Star className="w-5 h-5 text-[#FAB710]" />
                          <span className="font-medium text-gray-700">New Recovery+ Subscribers</span>
                        </div>
                        <span className="text-xl font-bold text-[#FAB710]">+{data?.period_stats.new_gold || 0}</span>
                      </div>
                    </div>
                    
                    <div className="bg-gradient-to-r from-[#1C3A7A]/10 to-[#1C3A7A]/5 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Crown className="w-5 h-5 text-[#1C3A7A]" />
                          <span className="font-medium text-gray-700">New Platinum Subscribers</span>
                        </div>
                        <span className="text-xl font-bold text-[#1C3A7A]">+{data?.period_stats.new_platinum || 0}</span>
                      </div>
                    </div>
                    
                    <div className="bg-gradient-to-r from-green-100 to-green-50 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <TrendingUp className="w-5 h-5 text-green-600" />
                          <span className="font-medium text-gray-700">Total New Subscriptions</span>
                        </div>
                        <span className="text-xl font-bold text-green-600">+{data?.period_stats.total_new || 0}</span>
                      </div>
                    </div>
                    
                    <div className="bg-gradient-to-r from-red-100 to-red-50 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <TrendingDown className="w-5 h-5 text-red-600" />
                          <span className="font-medium text-gray-700">Churned Subscriptions</span>
                        </div>
                        <span className="text-xl font-bold text-red-600">-{data?.period_stats.churned || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              </>
            )}

            {/* Trends Tab */}
            {activeTab === "trends" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trend</h3>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={data?.daily_trends || []}>
                        <defs>
                          <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#22C55E" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#22C55E" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis 
                          dataKey="date" 
                          tickFormatter={formatDate}
                          tick={{ fontSize: 12, fill: '#6B7280' }}
                        />
                        <YAxis 
                          tickFormatter={(v) => `$${v}`}
                          tick={{ fontSize: 12, fill: '#6B7280' }}
                        />
                        <Tooltip 
                          formatter={(value) => [`$${value.toFixed(2)}`, "Revenue"]}
                          labelFormatter={formatDate}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="revenue" 
                          stroke="#22C55E" 
                          strokeWidth={2}
                          fill="url(#revenueGradient)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">New Subscriptions by Tier</h3>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data?.daily_trends || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis 
                          dataKey="date" 
                          tickFormatter={formatDate}
                          tick={{ fontSize: 12, fill: '#6B7280' }}
                        />
                        <YAxis tick={{ fontSize: 12, fill: '#6B7280' }} />
                        <Tooltip labelFormatter={formatDate} />
                        <Legend />
                        <Bar dataKey="gold" name="Gold" fill={COLORS.gold} radius={[4, 4, 0, 0]} />
                        <Bar dataKey="platinum" name="Platinum" fill={COLORS.platinum} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            {/* Breakdown Tab */}
            {activeTab === "breakdown" && (
              <div className="space-y-6">
                {/* Tier Comparison */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Tier Comparison</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Free Tier */}
                    <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                          <Heart className="w-5 h-5 text-gray-500" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">Free</h4>
                          <p className="text-sm text-gray-500">$0/month</p>
                        </div>
                      </div>
                      <div className="text-3xl font-bold text-gray-900 mb-2">
                        {data?.distribution.free.count || 0}
                      </div>
                      <div className="text-sm text-gray-500">
                        {data?.distribution.free.percentage || 0}% of users
                      </div>
                      <div className="mt-4 h-2 bg-gray-200 rounded-full">
                        <div 
                          className="h-2 bg-gray-400 rounded-full transition-all"
                          style={{ width: `${data?.distribution.free.percentage || 0}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Recovery+ Tier */}
                    <div className="bg-gradient-to-br from-[#FAB710]/5 to-[#FAB710]/10 rounded-xl p-5 border border-[#FAB710]/30">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-[#FAB710]/20 rounded-full flex items-center justify-center">
                          <Star className="w-5 h-5 text-[#FAB710]" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">Recovery+</h4>
                          <p className="text-sm text-[#FAB710]">$9.99/month</p>
                        </div>
                      </div>
                      <div className="text-3xl font-bold text-gray-900 mb-2">
                        {data?.distribution.gold.count || 0}
                      </div>
                      <div className="text-sm text-gray-500">
                        {data?.distribution.gold.percentage || 0}% of users
                      </div>
                      <div className="mt-4 h-2 bg-[#FAB710]/20 rounded-full">
                        <div 
                          className="h-2 bg-[#FAB710] rounded-full transition-all"
                          style={{ width: `${Math.min((data?.distribution.gold.percentage || 0) * 5, 100)}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Recovery Elite Tier */}
                    <div className="bg-gradient-to-br from-[#1C3A7A]/5 to-[#1C3A7A]/10 rounded-xl p-5 border border-[#1C3A7A]/30">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-[#1C3A7A]/20 rounded-full flex items-center justify-center">
                          <Crown className="w-5 h-5 text-[#1C3A7A]" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">Recovery Elite</h4>
                          <p className="text-sm text-[#1C3A7A]">$19.99/month</p>
                        </div>
                      </div>
                      <div className="text-3xl font-bold text-gray-900 mb-2">
                        {data?.distribution.recovery_elite?.count || 0}
                      </div>
                      <div className="text-sm text-gray-500">
                        {data?.distribution.recovery_elite?.percentage || 0}% of users
                      </div>
                      <div className="mt-4 h-2 bg-[#1C3A7A]/20 rounded-full">
                        <div 
                          className="h-2 bg-[#1C3A7A] rounded-full transition-all"
                          style={{ width: `${Math.min((data?.distribution.recovery_elite?.percentage || 0) * 5, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Events */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Subscription Events</h3>
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {(data?.recent_events || []).length === 0 ? (
                          <tr>
                            <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                              No subscription events yet
                            </td>
                          </tr>
                        ) : (
                          data?.recent_events.map((event, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <span className="font-medium text-gray-900">{event.user_name}</span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                                  event.tier === 'recovery_elite' 
                                    ? 'bg-[#1C3A7A]/10 text-[#1C3A7A]' 
                                    : event.tier === 'recovery_plus'
                                      ? 'bg-[#FAB710]/10 text-[#FAB710]'
                                      : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {event.tier === 'recovery_elite' && <Crown className="w-3 h-3" />}
                                  {event.tier === 'recovery_plus' && <Star className="w-3 h-3" />}
                                  {event.tier === 'recovery_elite' ? 'Recovery Elite' : event.tier === 'recovery_plus' ? 'Recovery+' : event.tier}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                {event.expires_at 
                                  ? new Date(event.expires_at).toLocaleDateString()
                                  : '-'
                                }
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                {event.timestamp 
                                  ? new Date(event.timestamp).toLocaleString()
                                  : '-'
                                }
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </AdminLayout>
  );
};

export default AdminSubscriptions;
